import { RequestParameters, RequestReturnType, createAPIClient } from "@shopware/api-client";
import type { operationPaths, operations, components } from "../../../../node_modules/@shopware/api-client/api-types";
// @ts-ignore
import Cookies from "js-cookie";

const cookieConfig = {
    expires: 365, // days
    path: "/",
    sameSite: "lax",
};

export class Api {
    public currentLanguageId: string | undefined;

    // Cross-instance token-desync fix: every Vue component that does `new Api()` (Cart,
    // ShipmentSelector, VoucherInput, CartLineItems, CartCounter, AddToCartBtn, plus the
    // shopware mixin) used to get its own SDK client with its own snapshot of the
    // sw-context-token at instantiation time. When the Shopware backend rotated the token
    // in some response (e.g. after voucher application or any cart-mutating call), only the
    // SDK client that issued THAT request had its internal contextToken updated; sibling
    // clients on the page kept the old token. The cookie was kept in sync via
    // onContextChanged, but sibling SDK clients did NOT re-read it. The result was a
    // cross-instance desync: one component's probe could land on a fresh empty session
    // while the user's actual cart-with-voucher lived on a different token — yielding
    // empty deliveries, null prices, and the spinner-stuck-forever symptom on the price
    // column for non-active shipping methods after voucher application.
    //
    // Hoisting `client` to a static lazy singleton means every `new Api()` shares ONE SDK
    // client. Token rotations propagate to every caller automatically. No consumer changes
    // required — they still call `this.api.client.invoke(...)` exactly as before.
    private static _client: ReturnType<typeof createAPIClient<operations, operationPaths>> | null = null;

    public get client() {
        if (!Api._client) {
            Api._client = createAPIClient<operations, operationPaths>({
                baseURL: window.Shopware.StorefrontUrl,
                accessToken: window.Shopware.AccessToken,
                contextToken: Cookies.get("sw-context-token"),
                async onContextChanged(newContextToken) {
                    Cookies.set("sw-context-token", newContextToken, cookieConfig);
                }
            });
        }
        return Api._client;
    }

    public updateContext = async () => {
        const browserLanguage = document.querySelector('meta[name="iso-639-3166"]')?.getAttribute('value');
        const cookieLanguage = Cookies.get("sw-iso-639-3166");

        if (cookieLanguage && cookieLanguage.includes(browserLanguage)) {
            this.currentLanguageId = cookieLanguage.split('_')[0] || null;
            return;
        }

        const languages = await this.client.invoke('readLanguages post /language', {});

        if (languages?.elements && languages?.elements?.length > 0) {
            const currentLanguage = languages?.elements.find(l => l.translationCode?.code === browserLanguage);

            if (currentLanguage) {
                await this.client.invoke('updateContext patch /context', {
                    languageId: currentLanguage.id
                });

                Cookies.set("sw-iso-639-3166", `${currentLanguage.id}_${browserLanguage}`, cookieConfig);
                this.currentLanguageId = currentLanguage.id;
            }
        }
    }

    public getCookie = (cookieName: string) => Cookies.get(cookieName);
}

declare global {
    interface Window { Shopware: any; }
}

// reimport schemas to use it in application
export type ApiSchemas = components["schemas"];
// reimport operations request parameters to use it in application
export type ApiRequestParams<OPERATION_NAME extends keyof operations> =
    RequestParameters<OPERATION_NAME, operations>;
// reimport operations return types to use it in application
export type ApiReturnType<OPERATION_NAME extends keyof operations> =
    RequestReturnType<OPERATION_NAME, operations>;
